import type { SyntheticEvent } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { PriceBookSheetCell, PriceBookSheetRowView } from '@/document-render/domain/priceBook/buildPriceBookBySheetView';
import { priceBookSheetColumnLabel } from '@/document-render/domain/priceBook/priceBookMoneyLabels';
import type { PriceBookSubsectionKey } from '@/document-render/domain/priceBook/priceBookRowModel';
import type { LiveDocumentEditingConfig } from '@/document-render/presentation/editing/quote/LiveDocumentEditingConfig';
import { HIDDEN_OPACITY, rateBodyCellStyle, selectionOutline } from '../priceBookSheetStyles';
import { EditableRateCell } from './EditableRateCell';
import { PriceBookReveal } from './PriceBookReveal';
import { PriceBookRowControls } from './PriceBookRowControls';
import { priceBookRowSelectionId } from './priceBookEditing';
import { useCanvasReveal } from './useCanvasReveal';

export interface PriceBookQuoteRateRowProps {
  row: PriceBookSheetRowView;
  subsectionKey: PriceBookSubsectionKey;
  editing: LiveDocumentEditingConfig;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}

function isCellEditable(cell: PriceBookSheetCell, row: PriceBookSheetRowView, editing: LiveDocumentEditingConfig): boolean {
  if (!editing.onEditPriceBookCell) return false;
  if (cell.key === 'unit') return false;
  if (cell.key === 'name') return row.nameEditable;
  return true;
}

function rawValue(cell: PriceBookSheetCell, row: PriceBookSheetRowView): string {
  if (cell.key === 'name') return row.name;
  if (cell.key === 'unit') return cell.text;
  const value = row.values[cell.key];
  return value === null ? '' : String(value);
}

/** A customised rate row: selectable, editable money / name cells, reveal-on-hover controls. */
export function PriceBookQuoteRateRow({ row, subsectionKey, editing, selectedId, onSelect, ...moves }: PriceBookQuoteRateRowProps) {
  const selectionId = priceBookRowSelectionId(row.id);
  const sortable = useSortable({
    id: row.id,
    // A drag handle that cannot write anywhere must not exist: gated like the sheet sortable is.
    disabled: row.rowKind !== 'lineItem' || !editing.onReorderPriceBookLineItems
  });
  const { revealed, handlers } = useCanvasReveal();

  const select = (event: SyntheticEvent) => {
    event.stopPropagation();
    onSelect(selectionId);
  };

  return (
    <tr
      ref={sortable.setNodeRef}
      data-price-book-row-id={row.id}
      data-price-book-selection-id={selectionId}
      data-selected={selectedId === selectionId ? 'true' : undefined}
      onPointerDown={select}
      {...handlers}
      onFocus={(event) => {
        handlers.onFocus();
        select(event);
      }}
      style={{
        opacity: row.dimmed ? HIDDEN_OPACITY : undefined,
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
        ...selectionOutline(selectedId === selectionId)
      }}
    >
      {row.cells.map((cell, index) => (
        <td key={cell.key} style={rateBodyCellStyle(cell.align, cell.emphasis)}>
          {index === 0 ? (
            <PriceBookReveal revealed={revealed} side="left">
              <PriceBookRowControls
                row={row}
                editing={editing}
                handle={{
                  attributes: sortable.attributes,
                  listeners: sortable.listeners,
                  setActivatorNodeRef: sortable.setActivatorNodeRef
                }}
                {...moves}
              />
            </PriceBookReveal>
          ) : null}
          {isCellEditable(cell, row, editing) ? (
            <EditableRateCell
              label={`Edit ${priceBookSheetColumnLabel(subsectionKey, cell.key)} for ${row.name}`}
              value={rawValue(cell, row)}
              displayText={cell.text}
              numeric={cell.key !== 'name'}
              onCommit={(value) => editing.onEditPriceBookCell?.('sheet', row.id, cell.key, value)}
            />
          ) : (
            cell.text
          )}
        </td>
      ))}
    </tr>
  );
}
